import type { UnifiedOrderListItem } from '@flux/shared';
import { CheckCircle2, CloudDownload, MoreHorizontal, RotateCcw, Send, SendHorizontal, XCircle } from 'lucide-react';
import type { JSX } from 'react';
import { Button } from '@/components/ui/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { OrderActionDialogs } from './order-action-dialogs.js';
import type { OrderRowAction } from './order-status-actions.js';
import { useOrderActionController } from './use-order-action-controller.js';

const ACTION_LABELS: Record<OrderRowAction, string> = {
  'send-now': 'Envoyer maintenant',
  refresh: 'Mettre à jour depuis Shopify',
  regenerate: 'Renvoyer la commande à Martelet',
  ignore: 'Marquer ignorée',
  reactivate: 'Réactiver',
  'mark-handled': 'Marquer traitée',
};

const ACTION_ICONS: Record<OrderRowAction, JSX.Element> = {
  'send-now': <Send className="size-4" aria-hidden="true" />,
  refresh: <CloudDownload className="size-4" aria-hidden="true" />,
  regenerate: <SendHorizontal className="size-4" aria-hidden="true" />,
  ignore: <XCircle className="size-4" aria-hidden="true" />,
  reactivate: <RotateCcw className="size-4" aria-hidden="true" />,
  'mark-handled': <CheckCircle2 className="size-4" aria-hidden="true" />,
};

export interface OrderRowActionsProps {
  /** Ligne de la liste unifiée, côté commande Shopify (`item.order` non nul). */
  item: UnifiedOrderListItem;
}

/**
 * Tiret de la colonne Actions quand aucune action n'est disponible : même
 * boîte de 32 px que le bouton « … », pour que la colonne reste alignée à
 * droite de ligne en ligne.
 */
export function RowActionPlaceholder(): JSX.Element {
  return (
    <span className="inline-flex size-8 items-center justify-center text-muted-foreground">—</span>
  );
}

/**
 * Actions de ligne (`02` §3, table `01` §4) : menu compact, disponible selon
 * le statut de la commande. Comportement (confirmation, motif) porté par
 * `use-order-action-controller.ts`, partagé avec les boutons de l'en-tête du
 * détail (`order-header.tsx`).
 */
export function OrderRowActions({ item }: OrderRowActionsProps): JSX.Element {
  const controller = useOrderActionController({
    id: item.id,
    name: item.number,
    status: item.order?.status ?? 'scheduled',
    currentFileId: item.order?.currentFileId ?? null,
    currentFileName: item.order?.currentFileName ?? null,
  });

  if (item.order === null || controller.actions.length === 0) {
    return <RowActionPlaceholder />;
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Actions pour la commande ${item.number}`}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <MoreHorizontal aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {controller.actions.map((action) => (
            <DropdownMenuItem
              key={action}
              onSelect={() => {
                controller.trigger(action);
              }}
            >
              {ACTION_ICONS[action]}
              {ACTION_LABELS[action]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <OrderActionDialogs controller={controller} />
    </>
  );
}

export { ACTION_LABELS as ORDER_ACTION_LABELS, ACTION_ICONS as ORDER_ACTION_ICONS };
